import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import * as recipeActions from "../../redux/actions/recipeActions";
import * as userActions from "../../redux/actions/userActions";
import * as utils from "../../utils/stringHelpers";
import { bindActionCreators } from "redux";
import RecipeForm from "./RecipeForm";
import { toast } from "react-toastify";
import { fetchUserByEmail } from "../../api/userProfileApi";

const RecipeFormContainer = ({ actions, user, recipes, ...props }) => {
  const [errors, setErrors] = useState({});
  const [hasFetchedRecipes, setHasFetchedRecipes] = useState(false);
  const [recipe, setRecipe] = useState({});
  const [steps, setSteps] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [recipeAdmin, setRecipeAdmin] = useState([]);

  useEffect(() => {
    if (!user.signedIn) {
      actions.loadUser();
    }

    let selectedRecipe;
    const { firebase_uid, displayName } = user;

    if (firebase_uid && firebase_uid !== props.match.params.userID) {
      // TODO: this occasionally gets hit unexpectedly - possible due to token expiration? may have to add an additional step to reload/reauth the user before just throwing a 404
      props.history.push(`/cookbook/404`);
    }

    if (!hasFetchedRecipes) {
      actions
        .loadUserRecipes(firebase_uid)
        .then((recipes) => {
          selectedRecipe = props.match.params.id
            ? getRecipeById(props.match.params.id, recipes.recipes)
            : {};

          setRecipe({ ...selectedRecipe });

          if (!selectedRecipe) return;
          let recipeIngredients =
            Object.keys(selectedRecipe).length &&
            selectedRecipe.hasOwnProperty("ingredientList")
              ? selectedRecipe.ingredientList.map((ingredient, idx) => {
                  return {
                    ingredient: ingredient[0],
                    measurement: ingredient[1],
                    number: idx + 1,
                    saved: true,
                  };
                })
              : [];
          let recipeSteps =
            Object.keys(selectedRecipe).length &&
            selectedRecipe.hasOwnProperty("steps")
              ? selectedRecipe.steps.map((step, idx) => ({
                  number: idx + 1,
                  value: step,
                  saved: true,
                }))
              : [];
          let _recipeAdmin =
            Object.keys(selectedRecipe).length &&
            selectedRecipe.hasOwnProperty("recipeAdmin")
              ? selectedRecipe.recipeAdmin.map((admin) => ({
                  admin,
                }))
              : [];
          setSteps(recipeSteps);
          setIngredients(recipeIngredients);
          setRecipeAdmin(_recipeAdmin);
        })
        .catch((err) => {
          console.log(err);
          toast.error(
            `Sorry, ${displayName}, but we seem unable to load your recipes. Please try again later.`
          );
        });
        setHasFetchedRecipes(true)
    } else {
      selectedRecipe = props.match.params.id
        ? getRecipeById(props.match.params.id, recipes.recipes)
        : {};

      setRecipe({ ...selectedRecipe });
      if (!selectedRecipe) return;
      let recipeIngredients =
        Object.keys(selectedRecipe).length &&
        selectedRecipe.hasOwnProperty("ingredientList")
          ? selectedRecipe.ingredientList.map((ingredient, idx) => {
              return {
                ingredient: ingredient[0],
                measurement: ingredient[1],
                number: idx + 1,
                saved: true,
              };
            })
          : [];

      let recipeSteps =
        Object.keys(selectedRecipe).length &&
        selectedRecipe.hasOwnProperty("steps")
          ? selectedRecipe.steps.map((step, idx) => ({
              number: idx + 1,
              value: step,
              saved: true,
            }))
          : [];
      let recipeAdmin =
        Object.keys(selectedRecipe).length &&
        selectedRecipe.hasOwnProperty("recipeAdmin")
          ? selectedRecipe.recipeAdmin.map((admin) => ({
              admin,
            }))
          : [];
      setSteps(recipeSteps);
      setIngredients(recipeIngredients);
      setRecipeAdmin(recipeAdmin);
    }
}, [actions, hasFetchedRecipes, props.history, props.match.params.id, props.match.params.userID, recipes, user]);

  function getRecipeById(id, recipes) {
    if (!recipes.length) {
      return {};
    }
    return recipes.find((recipe) => recipe._id === id || null);
  }

  const formActions = {};

  formActions.addStep = () => {
    setSteps([...steps, { number: steps.length + 1, value: "", saved: false }]);
  };

  formActions.handleDelete = (e) => {
    e.preventDefault();
    toast.success(`Goodbye, ${recipe.title}!`);

    actions.deleteRecipe({ recipe }).then(() => {
      setIngredients([]);
      setSteps([]);
      props.history.push(`/cookbook/profile/${user.id}/recipe/`);
    });
  };

  formActions.addIngredient = () => {
    setIngredients([
      ...ingredients,
      {
        number: ingredients.length + 1,
        ingredient: "",
        measurement: "",
        saved: false,
      },
    ]);
  };

  formActions.deleteIngredient = (ingredient) => {
    if (!ingredient.saved) {
      const _ingredients = ingredients.filter(
        (_ingredient) => _ingredient.number !== ingredient.number
      );
      setIngredients(_ingredients);
      return;
    }

    actions.deleteRecipeIngredient(recipe._id, ingredient);
  };

  formActions.deleteStep = (step) => {
    if (!step.saved) {
      const _steps = steps.filter((_step) => _step.number != step.number);
      setSteps(_steps);
      return;
    }

    actions.deleteStep(recipe._id, step);
  };

  formActions.setDifficulty = (e) => {
    const { name, value } = e.target;
    setRecipe((prevRecipe) => ({
      ...prevRecipe,
      [name]: value,
    }));
  };

  formActions.sort = (step, sort) => {
    let currentIndex = step.number - 1;
    let targetIndex;
    if (sort === "up") {
      targetIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : 0;
    } else {
      targetIndex =
        currentIndex + 1 <= recipe.steps.length - 1
          ? currentIndex + 1
          : recipe.steps.length - 1;
    }
    let newRecipeSteps = steps.map((_step, idx) => {
      if (idx === targetIndex) {
        return step.value;
      } else if (idx === currentIndex) {
        return recipe.steps[targetIndex];
      } else {
        return _step.value;
      }
    });

    let newSteps = newRecipeSteps.map((newStep, idx) => {
      return { number: idx + 1, value: newStep, saved: false };
    });
    setRecipe((recipe) => ({
      ...recipe,
      steps: newRecipeSteps,
    }));
    setSteps(newSteps);
  };

  formActions.handleRecipeChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === "public") {
      setRecipe((prevRecipe) => ({
        ...prevRecipe,
        [name]: checked,
      }));
    } else if (name.split("_")[0] === "keywords") {
      let _keywords = [];
      let _value = name.split("_")[1];
      if (recipe.keywords) {
        _keywords = [...recipe.keywords];
        const valueIndex = _keywords.indexOf(_value);
        if (valueIndex > -1) {
          _keywords.splice(valueIndex, 1);
        } else {
          _keywords.push(_value);
        }
      } else {
        _keywords = [_value];
      }
      setRecipe((prevRecipe) => ({
        ...prevRecipe,
        keywords: _keywords,
      }));
    } else if (name.split("_")[0] === "step") {
      const _steps = steps.map((step) => {
        if (step.number === parseInt(name.split("_")[1])) {
          step.value = value;
          step.saved = false;
          return step;
        }
        return step;
      });
      setSteps(_steps);
    } else if (name.split("_")[0] === "ingredient") {
      const _ingredients = ingredients.map((ingredient) => {
        if (ingredient.number === parseInt(name.split("_")[1])) {
          if (name.split("_")[2] === "measure") {
            ingredient.measurement = value;
          } else {
            ingredient.ingredient = value;
          }
          return ingredient;
        }
        return ingredient;
      });
      setIngredients(_ingredients);
    } else {
      setRecipe((prevRecipe) => ({
        ...prevRecipe,
        [name]: value,
      }));
    }
  };

  formActions.handleSearch = async (recipeId, email) => {
    await actions.fetchUserByEmail(email).then(({ id }) => {
      if (id) {
        return actions.updateRecipeAdmin({
          recipeId: recipeId,
          userId: id,
          userEmail: email,
        });
      } else {
        //todo: display failure toast component communicating the the user doesn't exist
        console.log("display a toast for failure");
      }
    });
  };

  formActions.handleSaveRecipe = (e) => {
    e.preventDefault();

    if (!formIsValid()) {
      return;
    }
    const _ingredients = ingredients.map((ingredient) => [
      ingredient.ingredient,
      ingredient.measurement,
    ]);
    const _steps = steps.map((step) => step.value);

    const _recipe = Object.assign(recipe, {
      ingredientList: _ingredients,
      steps: _steps,
    });

    _recipe.slug = utils.createSlug(_recipe.title);
    actions.saveRecipe({ recipe: _recipe }).then(() => {
      if (!_recipe._id) {
        setRecipe({});
      }
      toast.success("Recipe has been saved.");
    });
  };

  function checkTitleErrors(title) {
    let error = null;
    if (!title) {
      error = "Recipe's require a title";
    } else if (utils.verifyChars(title)) {
      error = "Title must only include letters, numbers and spaces";
    } else if (utils.verifySlugRecipe(title, recipes.recipes)) {
      if (!props.match.params.id) {
        error = "Title must be unique to your cookbook";
      }
    }
    return error;
  }

  function formIsValid() {
    const { title, difficulty } = recipe;
    const errors = {};
    const titleError = checkTitleErrors(title);
    if (titleError) errors.title = titleError;
    if (!difficulty) errors.difficulty = "Difficulty level is required";
    if (!steps.length) errors.steps = "There is an error with your steps";
    steps.forEach((step) => {
      if (!step.value) errors.steps = "There is an error with your steps";
    });
    if (!ingredients.length)
      errors.ingredients = "There is an error with your ingredients";

    ingredients.forEach((ingredient) => {
      if (!ingredient.ingredient)
        errors.ingredients = "There is an error with your ingredients";
    });
    setErrors(errors);
    return Object.keys(errors).length === 0;
  }

  const userRecipeOptions = () => {
    let _keywords = recipe.keywords ? [...recipe.keywords] : [];
    let _options = {};
    let _opts = recipes.recipeOptions ? [...recipes.recipeOptions] : [];
    _opts.forEach((option) => {
      if (_keywords.indexOf(option) > -1) {
        _options[option] = true;
      } else {
        _options[option] = false;
      }
    });
    _options.recipeOptions = _opts;
    _options.recipeOptions.shift();
    return _options;
  };

  return (
    <RecipeForm
      formActions={formActions}
      steps={steps}
      ingredients={ingredients}
      recipe={recipe}
      admin={recipeAdmin}
      errors={errors}
      recipeOptions={userRecipeOptions()}
      userId={user.firebase_uid}
    />
  );
};

RecipeFormContainer.propTypes = {
  recipes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object,
  actions: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    title: state.title || "Cookbook",
    user: state.user,
    recipes: state.recipes,
    apiCalls: state.apiCallsInProgress,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: {
      loadUser: bindActionCreators(userActions.loadUser, dispatch),
      fetchUserByEmail: userActions.fetchUserByEmail,
      loadUserRecipes: bindActionCreators(
        recipeActions.loadUserRecipes,
        dispatch
      ),
      updateRecipeAdmin: bindActionCreators(
        recipeActions.updateRecipeAdmin,
        dispatch
      ),
      saveRecipe: bindActionCreators(recipeActions.saveRecipe, dispatch),
      deleteRecipe: bindActionCreators(recipeActions.deleteRecipe, dispatch),
      deleteRecipeIngredient: bindActionCreators(
        recipeActions.deleteRecipeIngredient,
        dispatch
      ),
      deleteStep: bindActionCreators(recipeActions.deleteStep, dispatch),
    },
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RecipeFormContainer);
